/**
 * ItemTable — library list view migrated to the shared DataTable
 * primitive (Epic 11.X.b). Same shape as ProjectsTable / AttackTreeList:
 *   <DataTable> + <TableToolbar search> + <TablePagination> + kebab
 *   <RowActionsMenu> per row (Edit / Delete, hidden when readOnly).
 *
 * Two library-specific deviations:
 *   1. Threat tree — when entityType === 'threat' the table renders a
 *      flattened parent/child list with expandable category rows.
 *      DataTable doesn't have native tree support; we pre-compute
 *      topLevelRows + a childrenByParent index, paginate TOP-LEVEL
 *      rows only, then splice each expanded category's children
 *      directly under their parent at render time. This keeps the
 *      tree intact across page boundaries (a parent's children are
 *      never split into a different page from the parent), so
 *      pagination works the same way for threats as for any other
 *      entity type. Multi-parent children get one row per parent
 *      occurrence (the @threatId suffix in _rowKey keeps each
 *      occurrence unique).
 *   2. Mixed-renderType columns — the existing tableColumns config
 *      uses `renderType` strings ('moduleRefs', 'impactRating', etc.).
 *      We bridge those to DataTable's `render: (row) => …` shape via
 *      a buildColumns() mapper that closes over referenceData.
 */
import React, { useState, useMemo, useEffect } from 'react';
import DataTable from '../../../components/ui/DataTable/DataTable';
import TableToolbar from '../../../components/ui/DataTable/TableToolbar';
import TablePagination from '../../../components/ui/DataTable/TablePagination';
import RowActionsMenu from '../../../components/ui/DataTable/RowActionsMenu';
import { Button } from '../../../components/ui/Button2';
import { Icon } from '../../../components/ui/Icon';
import { securityPropertyMap } from '../config/tableColumns';
import { getImpactColor } from '../../../utils/severityStyle';
import styles from '../styles/ItemTable.module.css';

const ITEMS_PER_PAGE_DEFAULT = 20;

// ────────────────────────────────────────────────────────────────────
// Cell renderers (auto-flipping tokens; the previous hardcoded
// cyan/white treatments broke in light mode).
// ────────────────────────────────────────────────────────────────────

const NoneText = () => <span className={styles.noneText}>None</span>;

const SecurityPropertiesCell = ({ properties }) => {
  if (!properties || properties.length === 0) return <NoneText />;
  return (
    <div className={styles.pillRow}>
      {properties.map((prop, index) => (
        <span key={index} className={styles.accentPill} title={prop}>
          {securityPropertyMap[prop] || prop}
        </span>
      ))}
    </div>
  );
};

const ReferencePillsCell = ({ refIds, refData, typeLabel }) => {
  if (!refIds || refIds.length === 0) return <NoneText />;
  if (!refData || refData.length === 0) {
    return <span className={styles.dimText}>{refIds.length} {typeLabel}(s)</span>;
  }

  const refMap = {};
  refData.forEach((ref) => { refMap[ref.item_id] = ref.name; });
  const names = refIds.map((id) => refMap[id]).filter(Boolean);

  if (names.length === 0) {
    return <span className={styles.dimText}>{refIds.length} {typeLabel}(s)</span>;
  }

  return (
    <div className={`${styles.pillRow} ${styles.pillRowWrap}`}>
      {names.map((name, index) => (
        <span key={index} className={`${styles.accentPill} ${styles.accentPillCompact}`}>{name}</span>
      ))}
    </div>
  );
};

const SubtypeCell = ({ subtype, subtypeOther }) => {
  if (subtype === 'Other' && subtypeOther) {
    const truncated = subtypeOther.length > 30 ? subtypeOther.substring(0, 30) + '...' : subtypeOther;
    return <span title={subtypeOther}>{truncated}</span>;
  }
  return subtype || <NoneText />;
};

const ImpactRatingCell = ({ rating }) => (
  <span style={{ color: getImpactColor(rating, { scheme: 'iosTones' }), fontWeight: 600 }}>
    {rating || 'N/A'}
  </span>
);

/* Feasibility rating colors carry over from the original (severity-
   styled hardcodes); deliberately mode-invariant since severity colors
   are a shared treatment across products. */
const FEASIBILITY_COLOR = { High: '#FF3B30', Medium: '#FF9500', Low: '#FFCC00', 'Very Low': '#34C759' };
const FeasibilityRatingCell = ({ rating }) => (
  <span style={{ color: FEASIBILITY_COLOR[rating] || 'currentColor', fontWeight: 600 }}>
    {rating || 'N/A'}
  </span>
);

const CALLevelCell = ({ cal }) => (
  <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{cal || 'N/A'}</span>
);

// ────────────────────────────────────────────────────────────────────
// Column conversion: tableColumns config (key/label/renderType/width/
// sortable) → DataTable column shape (key/label/render/width/sortable).
// ────────────────────────────────────────────────────────────────────

const renderTypeToCell = (renderType, item, referenceData) => {
  switch (renderType) {
    case 'securityProperties':
      return <SecurityPropertiesCell properties={item.entity_data?.securityProperties} />;
    case 'moduleRefs':
      return <ReferencePillsCell refIds={item.entity_data?.moduleRefs} refData={referenceData.modules} typeLabel="module" />;
    case 'featureRefs':
      return <ReferencePillsCell refIds={item.entity_data?.featureRefs} refData={referenceData.features} typeLabel="feature" />;
    case 'assetRefs':
      return <ReferencePillsCell refIds={item.entity_data?.assetRefs} refData={referenceData.assets} typeLabel="asset" />;
    case 'goalRefs':
      return <ReferencePillsCell refIds={item.entity_data?.goalRefs} refData={referenceData.goals} typeLabel="goal" />;
    case 'damageRefs':
      return <ReferencePillsCell refIds={item.entity_data?.damageRefs} refData={referenceData.damages} typeLabel="damage" />;
    case 'threatRefs':
      return <ReferencePillsCell refIds={item.entity_data?.threatRefs} refData={referenceData.threats} typeLabel="threat" />;
    case 'subtype':
      return <SubtypeCell subtype={item.entity_data?.subtype} subtypeOther={item.entity_data?.subtypeOther} />;
    case 'impactRating':
      return <ImpactRatingCell rating={item.entity_data?.finalImpactRating} />;
    case 'feasibilityRating':
      return <FeasibilityRatingCell rating={item.entity_data?.feasibilityRating} />;
    case 'calLevel':
      return <CALLevelCell cal={item.entity_data?.calLevel} />;
    default:
      return null;
  }
};

const defaultCellValue = (column, item) => {
  if (column.key === 'name') return item.name || 'Untitled';
  if (column.key === 'description') return item.description || '';
  const value = item.entity_data?.[column.key];
  if (value == null) return 'N/A';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

const ItemTable = ({
  items = [],
  columns: rawColumns = [],
  onEdit,
  onDelete,
  onCreateClick,
  createLabel,
  searchTerm = '',
  onSearchChange,
  entityType,
  referenceData = {},
  readOnly = false,
  loading = false,
}) => {
  const [sort, setSort] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_DEFAULT);

  const isThreatTree = entityType === 'threat';

  // Reset to page 1 whenever search / page-size change so the user
  // never lands on a now-out-of-range page.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage, entityType]);

  const toggleCategory = (threatId) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(threatId)) next.delete(threatId);
      else next.add(threatId);
      return next;
    });
  };

  // ── Search filter ───────────────────────────────────────────────────
  const filteredItems = useMemo(() => {
    if (!searchTerm) return items;
    const q = searchTerm.toLowerCase();
    return items.filter((item) =>
      rawColumns.some((column) => {
        let value;
        if (column.key === 'name') value = item.name;
        else if (column.key === 'description') value = item.description;
        else if (column.key === 'created_at') value = item.created_at;
        else if (item.entity_data && item.entity_data[column.key]) value = item.entity_data[column.key];

        if (value == null) return false;
        if (Array.isArray(value)) return value.some((v) => String(v).toLowerCase().includes(q));
        return String(value).toLowerCase().includes(q);
      }),
    );
  }, [items, rawColumns, searchTerm]);

  // ── Sort ────────────────────────────────────────────────────────────
  const sortedItems = useMemo(() => {
    if (!sort) return filteredItems;
    const { column, direction } = sort;
    return [...filteredItems].sort((a, b) => {
      let av;
      let bv;
      if (column === 'name') { av = a.name; bv = b.name; }
      else if (column === 'description') { av = a.description; bv = b.description; }
      else if (column === 'created_at') {
        av = new Date(a.created_at).getTime();
        bv = new Date(b.created_at).getTime();
      } else if (['assetRefs', 'moduleRefs', 'featureRefs', 'goalRefs'].includes(column)) {
        av = a.entity_data?.[column]?.length || 0;
        bv = b.entity_data?.[column]?.length || 0;
      } else {
        av = a.entity_data?.[column];
        bv = b.entity_data?.[column];
      }
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return direction === 'asc' ? -1 : 1;
      if (av > bv) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredItems, sort]);

  // ── Top-level rows + child index ──────────────────────────────────
  // In tree mode (threat, not searching) the "top-level rows" are
  // categories and uncategorized items. Children live in a separate
  // map keyed by their parent threat ID and get spliced in at render
  // time when their category is expanded.
  // In flat mode (non-threat OR threat-with-search) every sortedItem
  // is its own top-level row and the child map is empty.
  const { topLevelRows, childrenByParent } = useMemo(() => {
    if (!isThreatTree || searchTerm) {
      return {
        topLevelRows: sortedItems.map((item) => ({
          ...item,
          _rowKey: item.item_id,
          _isCategory: false,
          _parentId: null,
        })),
        childrenByParent: new Map(),
      };
    }

    const getParentIds = (item) => {
      const ed = item.entity_data;
      if (!ed) return [];
      if (Array.isArray(ed.parentThreatIds) && ed.parentThreatIds.length > 0) return ed.parentThreatIds;
      if (ed.parentThreatId) return [ed.parentThreatId];
      return [];
    };

    const cbp = new Map();
    for (const item of sortedItems) {
      const parents = getParentIds(item);
      for (const pid of parents) {
        if (!cbp.has(pid)) cbp.set(pid, []);
        cbp.get(pid).push(item);
      }
    }

    const tlr = [];
    for (const item of sortedItems) {
      const parents = getParentIds(item);
      if (parents.length > 0) continue;
      tlr.push({
        ...item,
        _rowKey: item.item_id,
        _isCategory: !!item.entity_data?.isCategory,
        _parentId: null,
      });
    }
    return { topLevelRows: tlr, childrenByParent: cbp };
  }, [sortedItems, isThreatTree, searchTerm]);

  // ── Pagination ─────────────────────────────────────────────────────
  // Pagination operates over TOP-LEVEL rows only, so a category's
  // expanded children always render directly under their parent on the
  // same page — never split across page boundaries.
  const paginatedTopLevel = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return topLevelRows.slice(start, start + itemsPerPage);
  }, [topLevelRows, currentPage, itemsPerPage]);

  // ── Display rows (paginated top-level + spliced-in children) ──────
  // For each top-level row on the current page, if it's an expanded
  // category we append its children right after it. A child with N
  // parents gets one row per parent occurrence (the @threatId suffix
  // in _rowKey keeps each occurrence unique).
  const displayRows = useMemo(() => {
    if (!isThreatTree || searchTerm) return paginatedTopLevel;

    const rows = [];
    for (const tl of paginatedTopLevel) {
      rows.push(tl);
      if (!tl._isCategory) continue;
      const threatId = tl.entity_data?.threatId;
      if (!expandedCategories.has(threatId)) continue;
      const children = childrenByParent.get(threatId) || [];
      for (const child of children) {
        rows.push({
          ...child,
          _rowKey: `${child.item_id}@${threatId}`,
          _isCategory: false,
          _parentId: threatId,
        });
      }
    }
    return rows;
  }, [paginatedTopLevel, isThreatTree, searchTerm, expandedCategories, childrenByParent]);

  // ── Build DataTable columns from the entity's tableColumns config ──
  // The first column gets a wrapper render that draws the expand
  // chevron for category rows and indents child rows.
  const columns = useMemo(() => {
    const tableCols = rawColumns.map((column, idx) => {
      const baseRender = (row) => {
        if (column.render) return column.render(row);
        if (column.renderType) {
          const cell = renderTypeToCell(column.renderType, row, referenceData);
          if (cell != null) return cell;
        }
        return defaultCellValue(column, row);
      };

      if (idx === 0 && isThreatTree) {
        return {
          key: column.key,
          label: column.label,
          width: column.width,
          sortable: column.sortable !== false,
          render: (row) => (
            <div
              className={styles.firstCell}
              style={row._parentId ? { paddingLeft: '1.5rem' } : undefined}
            >
              {row._isCategory && (
                <Icon
                  name="chevron-right"
                  size={14}
                  className={`${styles.expandChevron} ${expandedCategories.has(row.entity_data?.threatId) ? styles.expandChevronOpen : ''}`}
                />
              )}
              {baseRender(row)}
            </div>
          ),
        };
      }

      return {
        key: column.key,
        label: column.label,
        width: column.width,
        sortable: column.sortable !== false,
        render: baseRender,
      };
    });

    if (readOnly) return tableCols;

    return [
      ...tableCols,
      {
        /* Athena convention: kebab dropdown, empty header label. */
        key: '_actions',
        label: '',
        align: 'right',
        width: '60px',
        render: (row) => (
          <RowActionsMenu
            ariaLabel={`Actions for ${row.name || 'item'}`}
            actions={[
              {
                id: 'edit',
                label: 'Edit',
                icon: <Icon name="edit" size={16} />,
                onClick: () => onEdit?.(row),
              },
              {
                id: 'delete',
                label: 'Delete',
                danger: true,
                icon: <Icon name="delete" size={16} />,
                onClick: () => onDelete?.(row),
              },
            ]}
          />
        ),
      },
    ];
  }, [rawColumns, referenceData, isThreatTree, expandedCategories, readOnly, onEdit, onDelete]);

  const toolbar = (
    <TableToolbar
      /* Filter button hidden — library entries don't yet have a
         per-entity filter dimension wired through a FilterDrawer
         (unlike ProjectsTable's status filter). Once we add real
         filters (e.g. asset.type, threat.strideCategory) we'll flip
         showFilterButton back to true and pass onFilterToggle +
         filterButtonActive. */
      showFilterButton={false}
      search={{
        value: searchTerm,
        onChange: onSearchChange ?? (() => {}),
        placeholder: 'Search library items...',
      }}
      actions={
        !readOnly && onCreateClick && (
          <Button
            variant="default"
            onClick={onCreateClick}
            startIcon={<Icon name="add" size={14} />}
          >
            {createLabel || 'New Item'}
          </Button>
        )
      }
    />
  );

  /* Wrap DataTable in a flex-claiming container so it can claim its
     parent's height (LibraryContent's .tableArea is `flex: 1;
     min-height: 0`). Without this wrapper the DataTable would render
     at content height and the page-level scroll would kick in. */
  return (
    <div className={styles.tableHost}>
      <DataTable
        data={displayRows}
        columns={columns}
        rowKey="_rowKey"
        loading={loading}
        sort={sort}
        onSortChange={(column, direction) => setSort(column ? { column, direction } : null)}
        onRowClick={(row) => {
          if (row._isCategory) toggleCategory(row.entity_data?.threatId);
        }}
        toolbar={toolbar}
        pagination={
          <TablePagination
            currentPage={currentPage}
            totalItems={topLevelRows.length}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        }
        emptyState={{
          title: searchTerm ? 'No matching items' : 'No items to display',
          description: searchTerm
            ? `No items match "${searchTerm}".`
            : 'There are no items in this library section yet.',
        }}
      />
    </div>
  );
};

export default ItemTable;
